/**
 * View model for the shared public hospital profile.
 *
 * The only public source is the hostname-resolved tenant context from
 * GET admin/public/tenant/resolve. That payload includes the hospital name,
 * logo, and optional public address fields. Phone, description, departments,
 * and hours are not on it, so they are omitted rather than invented. Internal
 * fields such as appsCode are dropped and must not be rendered.
 */

function cleanText(value) {
  if (value == null) return "";
  return String(value).trim();
}

export function safePublicLogoUrl(value) {
  const url = cleanText(value);
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/") && !url.startsWith("//")) return url;
  if (/^data:image\/[a-z0-9.+-]+;base64,/i.test(url)) return url;
  return null;
}

/**
 * Formats public address components into display lines.
 * Returns null when nothing usable is present (caller hides the section).
 */
export function formatPublicHospitalAddress(address) {
  if (address == null || typeof address !== "object" || Array.isArray(address)) {
    return null;
  }
  const line1 = cleanText(address.line1);
  const line2 = cleanText(address.line2);
  const line3 = cleanText(address.line3);
  const city = cleanText(address.city);
  const state = cleanText(address.state);
  const postalCode = cleanText(address.postalCode);

  const street = [line1, line2, line3].filter(Boolean).join(", ");
  const cityState = [city, state].filter(Boolean).join(", ");
  const locality = [cityState, postalCode].filter(Boolean).join(" ");
  const lines = [street, locality].filter(Boolean);
  return lines.length ? lines : null;
}

export function buildPublicHospitalProfile(tenant) {
  const hospitalName = cleanText(tenant?.hospitalName);
  const logoUrl = tenant?.hasLogo ? safePublicLogoUrl(tenant.logoUrl) : null;
  return {
    hospitalName: hospitalName || null,
    logoUrl,
    addressLines: formatPublicHospitalAddress(tenant?.address),
  };
}
